import { ElementAlertStatus, ElementTarget, ElementType } from './index';
import React, { Fragment, useEffect, useState } from 'react';
import { alpha, Button, Theme } from '@mui/material';
import { Box, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import InfoIcon from 'components/element-viewer/controls-panel-icon/InfoIcon';
import DocumentsButton from 'components/element-viewer/controls-panel-icon/DocumentsButton';
import EditButton from 'components/element-viewer/controls-panel-icon/EditButton';
import DownloadIcon from 'components/element-viewer/controls-panel-icon/DownloadIcon';
import ShareButton from 'components/element-viewer/controls-panel-icon/ShareButton';
import { FullScreenHandle } from 'react-full-screen';
import FullScreenButton from 'components/element-viewer/controls-panel-icon/FullScreenButton';
import ExternalLinkButton from 'components/element-viewer/controls-panel-icon/ExternalLinkButton';
import FavoriteButton from 'components/element-viewer/controls-panel-icon/FavoriteButton';
import AlertSubscriptionIcon from 'components/element-viewer/controls-panel-icon/AlertSubscriptionIcon';
import { IBookmarksContext } from 'components/external-reference/hooks/useBookmarks';
import RatingIconButton from '../rating/RatingIconButton';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import BookmarkManager from 'components/external-reference/bookmark/BookmarManager';
import { BookmarkType } from 'components/external-reference/bookmark/BookmarkDropdown';
import useBundleTranslation from 'i18n';
import ActionMenu from 'components/common/action-menu/ActionMenu';
import EditManualData from 'components/element-viewer/controls-panel-icon/EditManualDataButton';
import ExpertAnalysis from 'components/element-viewer/controls-panel-icon/ExpertAnalysis';
import { ExternalReportViewerDataType } from 'app/extreport/ExternalReportViewer';
import TargetIcon from './controls-panel-icon/TargetIcon';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { useIsAdminOrHasPrivilege } from 'hooks/useUserPrivilege';
import LineageIconButton from 'components/lineage/LineageIconButton';

export interface IPartsVisibility {
    title?: boolean;
    control_buttons?: IIconsVisibility;
}
export interface IIconsVisibility {
    lineage?: boolean;
    favorite?: boolean;
    share?: boolean;
    download?: boolean;
    notification?: boolean;
    edit?: boolean;
    bookmark?: boolean;
}

export function useExternalReportNativeFilters(filters?: string, useNativeFilters?: boolean) {
    const [filtersToGet, setFiltersToGet] = useState(filters);
    useEffect(() => {
        if (useNativeFilters) {
            return;
        }
        setFiltersToGet(filters);
    }, [useNativeFilters, filters]);

    const listerIFrameFilters = (message: any) => {
        if (message?.data?.command != 'filters' || typeof message?.data?.filters == 'undefined') {
            if (filters != filtersToGet) {
                // Reset to default Filters
                setFiltersToGet(filters);
            }
            return false;
        }
        if (message.data.filters != filtersToGet) {
            // Update filters from IFrame response
            setFiltersToGet(JSON.stringify(message.data.filters));
        }
    };

    useEffect(() => {
        if (!useNativeFilters) {
            return;
        }
        const element = document.getElementById('iframe-component') as HTMLIFrameElement;
        if (element?.contentWindow) {
            element.contentWindow.postMessage({ command: 'getFilters' }, '*');
        }
        window.addEventListener('message', listerIFrameFilters);
        return () => {
            window.removeEventListener('message', listerIFrameFilters);
        };
    }, []);
    return filtersToGet;
}

export default function ControlsPanel({
    viewerRequestExtraData,
    element,
    segmentValueId,
    fullScreenHandle,
    externalLink,
    externalLinkTitle,
    onFavoritesChange,
    bookmarksContext,
    context,
    targets,
    alertStatus,
    userChartOverlay,
    filters,
    reference,
    visibility,
}: {
    viewerRequestExtraData?: ExternalReportViewerDataType;
    element: ElementType;
    segmentValueId: number;
    fullScreenHandle?: FullScreenHandle;
    externalLink?: string;
    externalLinkTitle?: string;
    onFavoritesChange: () => void;
    bookmarksContext?: IBookmarksContext;
    context?: 'viewer' | 'preview';
    targets?: ElementTarget[];
    alertStatus?: ElementAlertStatus;
    userChartOverlay?: number;
    filters?: string;
    reference?: string;
    visibility?: IIconsVisibility;
}) {
    const SeparatorItem = () => (
        <Box
            sx={{
                height: 16,
                width: '1px',
                backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.4),
                mx: 1.5,
            }}
        />
    );

    const downloadDisabled = useConfigConstantValue('DISABLE_DOWNLOADS');
    const allowedDownload = useIsAdminOrHasPrivilege('PRIV_ALLOW_DOWNLOAD');
    const showDownloadButton = (allowedDownload || !downloadDisabled) && visibility?.download !== false;

    const showLineageButton = useIsAdminOrHasPrivilege('PRIV_ACCESS_LINEAGE_DIAGRAM') && visibility?.lineage !== false;
    const hasLineageButton = useEmbeddingContextVisible('control_buttons.lineage');
    const hasIconButton = useEmbeddingContextVisible('control_buttons.info');

    const showFavorite = visibility?.favorite !== false;
    const showShare = element.permissions.share && visibility?.share !== false;
    const showNotification = visibility?.notification !== false;
    const showEdit = element.permissions.edit && visibility?.edit !== false;
    const showPreviewBookmark = bookmarksContext?.showIcon && visibility?.bookmark !== false;

    //for preview
    let settings: any = [];
    if (element.row.type == 'metric' || element.row.type == 'multi-metric chart') {
        if (context == 'preview') {
            settings = [
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                showLineageButton && hasLineageButton ? <SeparatorItem /> : null,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
                showShare ? <ShareButton element={element} segmentValueId={segmentValueId} /> : null,
                showDownloadButton ? (
                    <DownloadIcon uco={userChartOverlay} element={element} segmentValueId={segmentValueId} />
                ) : null,
                showNotification ? (
                    <AlertSubscriptionIcon
                        dataTest={'alert-subscription_button'}
                        elementId={element.row.elementId}
                        segmentValueId={segmentValueId}
                        elementType={element.row.type}
                        alertStatus={alertStatus}
                    />
                ) : null,
                element.row.dataFetchMethod == 'manual' && showEdit ? <EditManualData element={element} /> : null,
            ];
        } else {
            settings = [
                <InfoIcon element={element} />,
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                (showLineageButton && hasLineageButton) || hasIconButton ? <SeparatorItem /> : null,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
                showNotification ? (
                    <AlertSubscriptionIcon
                        elementId={element.row.elementId}
                        segmentValueId={segmentValueId}
                        elementType={element.row.type}
                        alertStatus={alertStatus}
                        dataTest={'alert-subscription_button'}
                    />
                ) : null,
                element.row.dataFetchMethod == 'manual' && showEdit ? <EditManualData element={element} /> : null,
                <DocumentsButton docs={element.documents} />,
                showDownloadButton ? (
                    <DownloadIcon uco={userChartOverlay} element={element} segmentValueId={segmentValueId} />
                ) : null,
                <TargetIcon targets={targets} />,
            ];
        }
    }
    if (element.row.type == 'external report') {
        if (context == 'preview') {
            settings = [
                showPreviewBookmark ? (
                    <BookmarkIcon
                        bookmarksContext={bookmarksContext}
                        elementId={element.row.elementId}
                        segmentValueId={segmentValueId}
                    />
                ) : null,
                showPreviewBookmark &&
                ((showLineageButton && hasLineageButton) ||
                    showFavorite ||
                    showShare ||
                    showDownloadButton ||
                    showNotification ||
                    externalLink) ? (
                    <SeparatorItem />
                ) : null,
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                showLineageButton && hasLineageButton ? <SeparatorItem /> : null,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
                showShare ? <ShareButton filters={filters} element={element} segmentValueId={segmentValueId} /> : null,
                showDownloadButton ? (
                    <DownloadIcon
                        useNativeFilters={viewerRequestExtraData?.additionalData?.useNativeFiltersInd}
                        filters={filters}
                        reference={reference}
                        element={element}
                        segmentValueId={segmentValueId}
                    />
                ) : null,
                showNotification ? (
                    <AlertSubscriptionIcon
                        elementId={element.row.elementId}
                        segmentValueId={segmentValueId}
                        elementType={element.row.type}
                        bookmarksContext={bookmarksContext}
                        alertStatus={alertStatus}
                    />
                ) : null,
                externalLink ? (
                    <ExternalLinkButton externalLinkTitle={externalLinkTitle} externalLink={externalLink} />
                ) : null,
            ];
        } else {
            settings = [
                <BookmarkIcon
                    bookmarksContext={bookmarksContext}
                    elementId={element.row.elementId}
                    segmentValueId={segmentValueId}
                />,
                <InfoIcon viewerRequestExtraData={viewerRequestExtraData} element={element} />,
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                <SeparatorItem />,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
                <DocumentsButton docs={element.documents} />,
                showDownloadButton ? (
                    <DownloadIcon
                        useNativeFilters={viewerRequestExtraData?.additionalData?.useNativeFiltersInd}
                        filters={filters}
                        reference={reference}
                        element={element}
                        segmentValueId={segmentValueId}
                    />
                ) : null,
                externalLink ? (
                    <ExternalLinkButton externalLinkTitle={externalLinkTitle} externalLink={externalLink} />
                ) : null,
                fullScreenHandle ? <FullScreenButton fullScreenHandle={fullScreenHandle} /> : null,
            ];
        }
    }
    if (element.row.type == 'internal report') {
        if (context == 'preview') {
            settings = [
                showPreviewBookmark ? (
                    <BookmarkIcon
                        bookmarksContext={bookmarksContext}
                        elementId={element.row.elementId}
                        segmentValueId={segmentValueId}
                    />
                ) : null,
                showPreviewBookmark &&
                ((showLineageButton && hasLineageButton) || showFavorite || showDownloadButton || showNotification) ? (
                    <SeparatorItem />
                ) : null,
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                showLineageButton && hasLineageButton ? <SeparatorItem /> : null,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
                showShare ? <ShareButton element={element} segmentValueId={segmentValueId} /> : null,
                showDownloadButton ? (
                    <DownloadIcon filters={filters} element={element} segmentValueId={segmentValueId} />
                ) : null,
                showNotification ? (
                    <AlertSubscriptionIcon
                        elementId={element.row.elementId}
                        segmentValueId={segmentValueId}
                        elementType={element.row.type}
                        bookmarksContext={bookmarksContext}
                        alertStatus={alertStatus}
                    />
                ) : null,
            ];
        } else {
            settings = [
                <InfoIcon element={element} />,
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                (showLineageButton && hasLineageButton) || hasIconButton ? <SeparatorItem /> : null,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
                <DocumentsButton docs={element.documents} />,
                showDownloadButton ? (
                    <DownloadIcon filters={filters} element={element} segmentValueId={segmentValueId} />
                ) : null,
                showNotification ? (
                    <AlertSubscriptionIcon
                        elementId={element.row.elementId}
                        segmentValueId={segmentValueId}
                        elementType={element.row.type}
                        bookmarksContext={bookmarksContext}
                        alertStatus={alertStatus}
                    />
                ) : null,
            ];
        }
    }
    if (element.row.type == 'other external content') {
        if (context == 'preview') {
            settings = [
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                showLineageButton && hasLineageButton ? <SeparatorItem /> : null,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
            ];
        } else {
            settings = [
                <InfoIcon element={element} />,
                showLineageButton && hasLineageButton ? (
                    <LineageIconButton
                        elementType={element.row.type}
                        elementId={element.row.elementId.toString()}
                        isPanelIcon
                    />
                ) : null,
                hasIconButton || (showLineageButton && hasLineageButton) ? <SeparatorItem /> : null,
                showFavorite ? (
                    <FavoriteButton element={element} segmentValueId={segmentValueId} onChange={onFavoritesChange} />
                ) : null,
                <DocumentsButton docs={element.documents} />,
            ];
        }
    }
    if (showEdit) {
        settings = [...settings, ...[<SeparatorItem />, <EditButton element={element} />]];
    }

    //SetUp actions menu
    let actionsMenuData: any = [];
    if (context != 'preview' && (element.row.type == 'metric' || element.row.type == 'multi-metric chart')) {
        if (showShare) {
            actionsMenuData = [
                {
                    key: 'share',
                    component: <ShareButton element={element} segmentValueId={segmentValueId} isActionMenuItem />,
                },
            ];
        }
        actionsMenuData.push({
            key: 'rating',
            component: (
                <RatingIconButton elementId={element.row.elementId.toString()} itemType={'element'} isActionMenuItem />
            ),
        });
    }
    if (context != 'preview' && element.row.type == 'external report') {
        if (showShare) {
            actionsMenuData = [
                {
                    key: 'share',
                    component: (
                        <ShareButton
                            filters={filters}
                            element={element}
                            segmentValueId={segmentValueId}
                            isActionMenuItem
                        />
                    ),
                },
            ];
        }
        actionsMenuData = actionsMenuData
            .concat(
                element.row.showCollaborationInd && element.permissions.expert_analysis
                    ? [
                          {
                              key: 'expert_analysis',
                              component: <ExpertAnalysis isActionMenuItem />,
                          },
                      ]
                    : [],
            )
            .concat([
                {
                    key: 'alert_subscription',
                    component: (
                        <AlertSubscriptionIcon
                            elementId={element.row.elementId}
                            segmentValueId={segmentValueId}
                            elementType={element.row.type}
                            bookmarksContext={bookmarksContext}
                            alertStatus={alertStatus}
                            isActionMenuItem
                        />
                    ),
                },
                {
                    key: 'rating',
                    component: (
                        <RatingIconButton
                            elementId={element.row.elementId.toString()}
                            itemType={'element'}
                            isActionMenuItem
                        />
                    ),
                },
            ]);
    }
    if (context != 'preview' && element.row.type == 'internal report' && showShare) {
        actionsMenuData = [
            {
                key: 'share',
                component: <ShareButton element={element} segmentValueId={segmentValueId} isActionMenuItem />,
            },
        ];
    }
    if (context != 'preview' && element.row.type == 'other external content') {
    }

    const hasActionsMenu = useEmbeddingContextVisible('action_select');

    if (hasActionsMenu && actionsMenuData.length) {
        settings = [
            ...settings,
            ...[
                <SeparatorItem />,
                <span id={'top-actions-menu'} data-test={'top-actions-menu'}>
                    <ActionMenu items={actionsMenuData} />
                </span>,
            ],
        ];
    }

    const hasActionsBar = useEmbeddingContextVisible('actionsBar');
    if (!hasActionsBar) {
        return null;
    }
    return (
        <Stack direction="row" alignItems="center">
            {settings.map((component: any, i: number) => (
                <Fragment key={i}>{component}</Fragment>
            ))}
        </Stack>
    );
}

function BookmarkIcon({
    bookmarksContext,
    elementId,
    segmentValueId,
}: {
    bookmarksContext?: IBookmarksContext;
    elementId: number;
    segmentValueId: number;
}) {
    const { t } = useBundleTranslation([
        'components/external-reference/bookmark',
        'extreport',
        'components/external-reference/filter_block',
    ]);

    const defaultPopupSettings: PopupSettings = {
        title: t('bookmarks'),
        textOK: t('form:button_done'),
        noCancel: true,
    };

    const [popupSettings, setPopupSettings] = useState(defaultPopupSettings);
    const [managerPopup, setManagerPopup] = useState<boolean>(false);

    if (!bookmarksContext?.showIcon) {
        return null;
    }

    const handleManagerOnEditMode = (onEditMode: boolean) => {
        if (onEditMode) {
            setPopupSettings({ ...defaultPopupSettings, noButtons: true });
        } else {
            setPopupSettings(defaultPopupSettings);
        }
    };

    const handleHideManager = function () {
        setManagerPopup(false);
    };
    const handleShowManager = function () {
        setManagerPopup(true);
    };

    return (
        <div>
            <Button
                aria-haspopup="true"
                onClick={handleShowManager}
                variant="outlined"
                color="primary"
                className={'min-width--icon'}
            >
                <IconMi icon="bookmark" fontSize="16" />
            </Button>

            <Popup maxWidth="popupMd" settings={popupSettings} onHide={handleHideManager} open={managerPopup}>
                <BookmarkManager
                    managerContextType={'bookmark'}
                    setOnEditMode={handleManagerOnEditMode}
                    elementId={elementId}
                    segmentValueId={segmentValueId}
                    provideLink
                    bookmarks={bookmarksContext.bookmarks}
                    filters={bookmarksContext.filters}
                    siblingTabs={bookmarksContext.siblingTabs}
                    onDeleteClick={bookmarksContext.APIDeleteBookmark}
                    onDuplicateClick={bookmarksContext.APIDuplicateBookmark}
                    onSortEnd={bookmarksContext.APISortBookmarks}
                    onUpdateClick={(bookmark: BookmarkType) =>
                        bookmarksContext?.APIUpdateBookmark(bookmark, bookmarksContext.selectedReferenceId)
                    }
                />
            </Popup>
        </div>
    );
}
